import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Skeleton } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Callout, CardPreOferta, PageHeader, StepActions } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useLazyGetConditionsByIdQuery,
  useLazyGetLoanApplicationByIdQuery,
  usePostValidateNextStageMutation,
} from "@api/applications";
import { useLazyGetLeasingTypeConfigQuery } from "@api/catalogs";
import { useLazyGetLeasingListQuery } from "@api/leasing";
import { ApiError } from "@interfaces/response";
import { LeasingMedicalEquipmentDetails, LeasingStructure } from "@interfaces/LeasingTypes";
import { formatCurrencyWhole } from "@helpers/funciones";
import { STATUS_CATALOG } from "@helpers/constantes";
import useStatusConfig from "@hooks/useStatusConfig";

/** Datos que muestra la pre-oferta. Lo que no venga del backend se muestra en 0. */
interface PreOferta {
  /** Nombre del tipo de equipo (catálogo de leasing types), ej. "Imagenología". */
  equipo: string;
  descripcion: string;
  renta: number;
  valor: number;
  anticipo: number;
  plazo: number;
}

const PRE_OFERTA_VACIA: PreOferta = { equipo: "", descripcion: "", renta: 0, valor: 0, anticipo: 0, plazo: 0 };

/**
 * Pre-oferta de arrendamiento de Mercader (`S_PreOfertaArrend` del diseño): habla de renta, plazo y equipo,
 * no de crédito. Es de solo lectura: repite lo que el usuario cotizó y lo confirma con su ejecutivo.
 */
export const OfertaMercader = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { nextStatus } = useStatusConfig(STATUS_CATALOG.oferta.code);
  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const [triggerPostNextStage] = usePostValidateNextStageMutation();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getLeasingList] = useLazyGetLeasingListQuery();
  const [getLoanApplication] = useLazyGetLoanApplicationByIdQuery();
  const [getLeasingTypeConfig] = useLazyGetLeasingTypeConfigQuery();

  const [preOferta, setPreOferta] = useState<PreOferta>(PRE_OFERTA_VACIA);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { equipo, descripcion, renta, valor, anticipo, plazo } = preOferta;

  // El plazo sale de la condición activa (mismo servicio que `OfertaDefault`); renta, valor y anticipo, del
  // `details` del leasing, y el tipo de equipo, del `leasing_type_id` de la solicitud buscado en el catálogo
  // de leasing types de su régimen (el mismo que usa el cotizador). Lo que no venga se toma como 0 o vacío.
  useEffect(() => {
    const fetchPreOferta = async () => {
      try {
        setIsLoading(true);
        const loanApplicationId = String(application.id);
        const [conditions, leasings, loanApplication] = await Promise.all([
          getConditions(loanApplicationId).unwrap(),
          getLeasingList(loanApplicationId).unwrap() as Promise<LeasingStructure<LeasingMedicalEquipmentDetails>[]>,
          getLoanApplication(loanApplicationId).unwrap(),
        ]);
        const activeCondition = conditions.find((item) => item.active);
        const details = leasings[0]?.details;
        let equipo = "";
        if (loanApplication.leasing_type_id && loanApplication.tax_systems_id) {
          const leasingTypes = await getLeasingTypeConfig({
            tax_system_id: String(loanApplication.tax_systems_id),
          }).unwrap();
          equipo =
            leasingTypes.find((item) => item.leasing_type_id === loanApplication.leasing_type_id)?.leasing_type?.name ??
            "";
        }

        setPreOferta({
          equipo,
          descripcion: details?.equipment_description?.trim() ?? "",
          renta: details?.monthly_rent ?? 0,
          valor: details?.amount_finance ?? 0,
          anticipo: details?.down_payment ?? 0,
          plazo: activeCondition?.selected_term ?? 0,
        });
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "OfertaMercader-preoferta" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    };

    fetchPreOferta();
  }, [application.id, getConditions, getLeasingList, getLoanApplication, getLeasingTypeConfig, dispatch]);

  // "Aceptar pre-oferta" solo avanza al siguiente status de `status_config` y navega a su pantalla.
  const handleAccept = async () => {
    try {
      setIsSubmitting(true);
      if (!nextStatus) {
        throw new Error("No se encontró el siguiente status");
      }
      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: nextStatus.code,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();
      navigate(nextStatus.url);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "OfertaMercader-accept" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box>
      <PageHeader
        eyebrow="Resultado · Pre-oferta"
        title="¡Tu arrendamiento está pre-aprobado!"
        subtitle="Con la información de tu práctica y tu buró, calificas para arrendar el equipo en las condiciones que cotizaste. La oferta final la confirmas con tu ejecutivo."
      />

      {isLoading ? (
        <Skeleton variant="rounded" animation="wave" height={260} sx={{ borderRadius: "14px" }} />
      ) : (
        <CardPreOferta
          variant="flat"
          // Como el diseño: el tipo de equipo (o "Equipo médico" si no se encuentra) y la descripción capturada.
          eyebrow={`Tu pre-oferta · ${equipo || "Equipo médico"}${descripcion ? ` · ${descripcion}` : ""}`}
          badge="Pre-aprobado"
          amount={formatCurrencyWhole(renta)}
          amountCaption="MXN + IVA / mes"
          sub={`Renta mensual · ${plazo} meses · arrendamiento puro, sin valor residual · Vigente por 30 días`}
          items={[
            { label: "Valor del equipo (sin IVA)", value: formatCurrencyWhole(valor) },
            { label: "Anticipo", value: formatCurrencyWhole(anticipo) },
            { label: "Plazo", value: `${plazo} meses` },
            { label: "Total de rentas", value: formatCurrencyWhole(renta * plazo) },
          ]}
        />
      )}

      <Box sx={{ mt: "14px" }}>
        <Callout tone="info" compact icon={<InfoOutlined sx={{ fontSize: 14 }} />}>
          Pre-oferta informativa, sujeta a la cotización final del proveedor y a la validación de tu expediente. Las
          rentas son <strong>100% deducibles</strong> para tu práctica.
        </Callout>
      </Box>

      <StepActions
        onNext={handleAccept}
        nextLabel="Aceptar pre-oferta"
        nextDisabled={isLoading || isSubmitting}
        nextTestId="oferta-mercader-aceptar-button"
      />
    </Box>
  );
};
